/**
 * Quiz diagnóstico para deportistas — identifica los padecimientos más frecuentes
 * tratados en Terapia Física. NO se muestra en el menú; solo accesible vía link directo.
 *
 * Cada pregunta marca uno o más padecimientos. Al final, sumamos los flags por condición
 * y mostramos las 3 más probables al usuario.
 */

export const DEPORTISTA_QUIZ = {
  id: "deportistas-evaluacion",
  title: "Evaluación deportiva integral",
  intro:
    "Si entrenas, compites o llevas una vida activa, este quiz te ayuda a identificar señales de los " +
    "padecimientos más comunes que tratamos en Terapia Física. 10 preguntas, 2 minutos.",
  therapist: {
    name: "Fisioterapeuta Omar Aguilera",
    role: "Fisioterapeuta y Quiropráctico",
    service: "terapia_fisica",
    phoneWa: "527712141634",
    photo: "/img/team/therapist_1_quiro_fisio.png",
  },
};

// Cada pregunta tiene "flags": padecimientos que se incrementan cuando el usuario responde "Sí"
export const DEPORTISTA_QUESTIONS = [
  {
    id: "q1",
    text: "Después de entrenar (o al día siguiente), sientes dolor o rigidez en la espalda baja que dura más de 24 horas.",
    flags: ["lumbalgia"],
  },
  {
    id: "q2",
    text: "Has sentido dolor que se irradia desde la espalda baja hacia un glúteo, muslo o pierna.",
    flags: ["ciatica", "hernia_lumbar"],
  },
  {
    id: "q3",
    text: "Sientes dolor o tensión en cuello/hombros al levantar peso, hacer push-ups, o después de muchas horas frente a la pantalla.",
    flags: ["cervicalgia", "tendinitis_hombro"],
  },
  {
    id: "q4",
    text: "Tienes dolor punzante en un hombro al levantar el brazo arriba de la cabeza (saques, lanzamientos, press militar).",
    flags: ["tendinitis_hombro"],
  },
  {
    id: "q5",
    text: "Tienes dolor en la parte externa o interna del codo al agarrar pesas, raqueta o palos (golf, tenis, halterofilia).",
    flags: ["epicondilitis"],
  },
  {
    id: "q6",
    text: "Te truena o se traba la rodilla al ponerte en cuclillas, bajar escaleras o tras correr distancias largas.",
    flags: ["meniscopatia", "condromalacia"],
  },
  {
    id: "q7",
    text: "Sientes dolor en la parte delantera de la rodilla (justo debajo de la rótula) tras correr, saltar o ciclismo.",
    flags: ["condromalacia", "tendinitis_rotuliana"],
  },
  {
    id: "q8",
    text: "Has tenido un esguince de tobillo en los últimos 12 meses (o varios a lo largo del tiempo).",
    flags: ["esguince_cronico"],
  },
  {
    id: "q9",
    text: "Tienes dolor agudo en el talón o planta del pie al dar los primeros pasos después de despertar o levantarte.",
    flags: ["fascitis_plantar"],
  },
  {
    id: "q10",
    text: "Cuando entrenas piernas o haces sentadillas, sientes molestia o dolor en la parte lateral de la cadera o glúteo.",
    flags: ["bursitis_cadera", "ciatica"],
  },
];

export const CONDITIONS = {
  lumbalgia: {
    name: "Lumbalgia mecánica",
    short: "Dolor lumbar",
    description:
      "Es el dolor más común en deportistas y oficinistas. Sobrecarga de la musculatura paravertebral, falta de movilidad de cadera o desbalance entre core y glúteos.",
    treatment:
      "Liberación miofascial + ejercicio terapéutico + corrección de patrón de fuerza. En 4-6 sesiones se resuelve en el 80% de los casos.",
    severity: "mid",
  },
  ciatica: {
    name: "Ciática (irritación del nervio ciático)",
    short: "Ciática",
    description:
      "Dolor que viaja desde la espalda baja por el glúteo y muslo. Puede ser por compresión discal, contractura del piramidal o desequilibrio postural.",
    treatment:
      "Manipulación quiropráctica suave + estiramiento neural + fortalecimiento profundo. Importante atender pronto para no cronificar.",
    severity: "high",
  },
  hernia_lumbar: {
    name: "Posible hernia discal lumbar",
    short: "Hernia discal lumbar",
    description:
      "El dolor irradiado intenso, hormigueo o debilidad en la pierna puede indicar compresión radicular. Requiere valoración con estudios.",
    treatment:
      "El 90% de las hernias responden a terapia conservadora: tracción + ejercicios McKenzie + reeducación postural. Solo el 5-10% requiere cirugía.",
    severity: "high",
  },
  cervicalgia: {
    name: "Cervicalgia mecánica",
    short: "Dolor cervical",
    description:
      "Tensión muscular en cuello y trapecio por postura, estrés o sobrecarga deportiva. Puede generar migrañas tensionales y mareos cervicogénicos.",
    treatment:
      "Masaje terapéutico profundo + movilización cervical + ejercicios estabilizadores. Excelente respuesta en 3-5 sesiones.",
    severity: "mid",
  },
  tendinitis_hombro: {
    name: "Tendinitis del manguito rotador",
    short: "Hombro de nadador / pitcher",
    description:
      "Inflamación de los tendones que estabilizan el hombro. Muy frecuente en nadadores, lanzadores, voleibolistas y crossfiteros.",
    treatment:
      "Liberación + ultrasonido + ejercicios de manguito + reeducación de movimiento sobre la cabeza. Cero levantamientos pesados hasta resolver.",
    severity: "mid",
  },
  epicondilitis: {
    name: "Epicondilitis (codo de tenista/golfista)",
    short: "Codo de tenista / golfista",
    description:
      "Microlesiones en los tendones del codo por agarre repetitivo. Aparece tras subir cargas, cambiar de raqueta o aumentar entrenamiento.",
    treatment:
      "Crioterapia + láser + ejercicios excéntricos + corrección de técnica. Bandeja epicondilar como soporte.",
    severity: "mid",
  },
  meniscopatia: {
    name: "Posible lesión meniscal",
    short: "Lesión de menisco",
    description:
      "El bloqueo o chasquido de rodilla puede indicar daño meniscal. Común en fútbol, baloncesto, crossfit y artes marciales.",
    treatment:
      "Reposo activo + fortalecimiento de cuádriceps y glúteo medio. Si hay bloqueo persistente, valorar con resonancia.",
    severity: "high",
  },
  condromalacia: {
    name: "Condromalacia rotuliana",
    short: "Rodilla del corredor",
    description:
      "Desgaste o reblandecimiento del cartílago de la rótula. Típica en runners, ciclistas y triatletas con desbalance VMO/VL.",
    treatment:
      "Reeducación de la pisada + fortalecimiento del cuádriceps interno + vendaje neuromuscular. Reducción de impacto temporal.",
    severity: "mid",
  },
  tendinitis_rotuliana: {
    name: "Tendinitis rotuliana (jumper's knee)",
    short: "Rodilla del saltador",
    description:
      "Inflamación del tendón rotuliano por sobrecarga en deportes de salto: voleibol, baloncesto, plyometrics, parkour.",
    treatment:
      "Ejercicio excéntrico tipo Curwin + ondas de choque (si aplica) + reducción temporal del volumen de salto.",
    severity: "mid",
  },
  esguince_cronico: {
    name: "Inestabilidad crónica de tobillo",
    short: "Tobillo inestable",
    description:
      "Tras varios esguinces, los ligamentos quedan elongados y propensos a re-lesionarse. Riesgo alto de torceduras recurrentes.",
    treatment:
      "Propiocepción + fortalecimiento de peroneos + vendaje funcional. Programa de 6 semanas reduce 70% el riesgo de re-esguince.",
    severity: "mid",
  },
  fascitis_plantar: {
    name: "Fascitis plantar",
    short: "Talón doloroso",
    description:
      "Inflamación de la fascia plantar por impacto repetido, mala pisada o calzado inadecuado. Muy común en runners y CrossFit.",
    treatment:
      "Liberación miofascial + estiramientos específicos + plantilla temporal + ondas de choque si crónica.",
    severity: "mid",
  },
  bursitis_cadera: {
    name: "Bursitis trocantérea",
    short: "Bursitis de cadera",
    description:
      "Inflamación de la bursa lateral de la cadera por desbalance de glúteo medio. Frecuente en runners y triatletas.",
    treatment:
      "Fortalecimiento de glúteo medio + corrección de marcha + crioterapia. Excelente pronóstico con tratamiento dirigido.",
    severity: "mid",
  },
};

export function scoreConditions(answers) {
  // answers: arreglo de booleanos en orden de DEPORTISTA_QUESTIONS
  const counts = {};
  DEPORTISTA_QUESTIONS.forEach((q, i) => {
    if (!answers[i]) return;
    q.flags.forEach((f) => {
      counts[f] = (counts[f] || 0) + 1;
    });
  });
  const ranked = Object.entries(counts)
    .map(([key, score]) => ({ key, score, ...CONDITIONS[key] }))
    .sort((a, b) => b.score - a.score);
  return ranked;
}

export function overallSeverity(ranked) {
  // Si hay al menos un padecimiento marcado como "high" presente → rojo
  // Si hay 3+ padecimientos detectados → amarillo
  // En otros casos → verde
  if (ranked.some((c) => c.severity === "high")) return "red";
  if (ranked.length >= 3) return "yellow";
  if (ranked.length >= 1) return "yellow";
  return "green";
}
